import { useAbortController } from '@harvey-frontend/providers/abort-controller-provider'
import { useMutation } from '@tanstack/react-query'
import { useShallow } from 'zustand/react/shallow'

import { useAccessTokenContext } from '../providers/AccessTokenProvider'
import useAppStore from '../stores/store'
import {
  fetchPlaybookRuleSuggestions,
  PlaybookEditSuggestion,
} from '../utils/playbook-utils'

const ABORT_CONTROLLER_KEY = 'playbook-rule-suggestions'

export interface PlaybookRuleSuggestionsParams {
  ruleName: string
  clauseText: string
  standardPosition?: string
  acceptableDeviations?: string[]
  referenceId: string
  abortController: AbortController
}

export interface PlaybookRuleSuggestionsResponse {
  standardSuggestions: PlaybookEditSuggestion | undefined
  acceptableDeviationsSuggestions: PlaybookEditSuggestion | undefined
}

export enum SuggestionType {
  STANDARD = 'Standard position',
  FALLBACK = 'Fallback',
}

/**
 * Fetches suggestions for a playbook rule.
 * @returns Standard and acceptable deviations suggestions.
 * Note: this only returns a SINGLE suggestion for each type.
 * If more than one is returned, the first one will be chosen.
 */
export const usePlaybookRuleSuggestions = () => {
  const { getAccessToken } = useAccessTokenContext()
  const { addAbortController, removeAbortController } = useAbortController()
  const { documentId, eventId, addSuggestionsForReference } = useAppStore(
    useShallow((s) => ({
      documentId: s.documentId,
      eventId: s.eventId,
      addSuggestionsForReference: s.addSuggestionsForReference,
    }))
  )

  return useMutation<
    PlaybookRuleSuggestionsResponse,
    Error,
    PlaybookRuleSuggestionsParams
  >({
    mutationFn: async ({
      ruleName,
      clauseText,
      standardPosition,
      acceptableDeviations = [],
      referenceId,
      abortController,
    }) => {
      const accessToken = await getAccessToken()
      if (!accessToken) {
        throw new Error('No access token provided')
      }

      addAbortController(ABORT_CONTROLLER_KEY, abortController)

      const queries: Promise<PlaybookEditSuggestion[]>[] = []

      if (standardPosition) {
        queries.push(
          fetchPlaybookRuleSuggestions(
            documentId,
            eventId,
            clauseText,
            standardPosition,
            accessToken,
            SuggestionType.STANDARD,
            abortController
          )
        )
      }

      const acceptableQueries = acceptableDeviations.map((deviation) =>
        fetchPlaybookRuleSuggestions(
          documentId,
          eventId,
          clauseText,
          deviation,
          accessToken,
          SuggestionType.FALLBACK,
          abortController
        )
      )

      const [standardResults, acceptableResults] = await Promise.all([
        standardPosition ? Promise.all([queries[0]]) : Promise.resolve([]),
        Promise.all(acceptableQueries),
      ])

      const flattenedStandard = standardResults.flat()
      const flattenedAcceptable = acceptableResults.flat()

      const suggestions = {
        standardSuggestions: flattenedStandard.length
          ? flattenedStandard[0]
          : undefined,
        acceptableDeviationsSuggestions: flattenedAcceptable.length
          ? flattenedAcceptable[0]
          : undefined,
      }

      if (suggestions.standardSuggestions) {
        addSuggestionsForReference(ruleName, referenceId, [
          suggestions.standardSuggestions,
        ])
      }
      if (suggestions.acceptableDeviationsSuggestions) {
        addSuggestionsForReference(ruleName, referenceId, [
          suggestions.acceptableDeviationsSuggestions,
        ])
      }

      return suggestions
    },
    onSettled: () => {
      removeAbortController(ABORT_CONTROLLER_KEY)
    },
  })
}
